import { useEffect, useState } from 'react';
import { isSpecialRole, type RoomView, type SpecialRole } from '@bm/shared';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { wsClient } from '@/lib/wsClient';
import { useWakeLock } from '@/lib/wakeLock';
import { avatarColor } from '@/lib/avatar';
import { haptics } from '@/lib/haptics';
import { AppShell } from '@/components/AppShell';
import { GameHeader } from '@/components/GameHeader';
import { ConfirmSheet } from '@/components/ConfirmSheet';
import { Sheet } from '@/components/Sheet';
import { Timer } from '@/components/Timer';
import { Button } from '@/components/Button';

const ABSTAIN = '__ABSTAIN__';

/** Fähigkeiten mit Ziel öffnen einen eigenen Dialog, die anderen bestätigen nur. */
const NEEDS_TARGET: Record<SpecialRole, boolean> = {
  BLESSING: true,
  VOTE_BLOCK: true,
  DEAD_REDEMPTION: false,
  DOUBLE_UP: false,
};

export function VotingScreen() {
  const view = useAppStore((s) => s.view);
  const [selection, setSelection] = useState<string | null>(null);
  const [abilityOpen, setAbilityOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** Abgeschickt, aber der Server-Snapshot ist noch nicht zurück. */
  const [submitting, setSubmitting] = useState(false);
  useWakeLock(true);

  const yourVote = view?.ballot?.yourVote ?? null;
  // Sobald die Stimme im Snapshot steht, übernimmt der `alreadyVoted`-Zweig.
  useEffect(() => {
    if (yourVote !== null) setSubmitting(false);
  }, [yourVote]);

  if (!view || !view.ballot) return null;

  const ballot = view.ballot;
  const totalSeconds = ballot.isTieBreaker ? view.settings.tieBreakerSeconds : view.settings.votingSeconds;
  const alreadyVoted = ballot.yourVote !== null;
  // `canVote` statt `alive`: „Letztes Wort" darf auch als Ausgeschiedener wählen.
  const canVote = view.self.canVote && !alreadyVoted;

  const role = view.self.role;
  const specialRole = role !== null && isSpecialRole(role) ? role : null;
  const selectedName =
    selection && selection !== ABSTAIN
      ? (view.players.find((player) => player.id === selection)?.name ?? '')
      : null;

  const submit = () => {
    if (selection === null) return;
    setConfirmOpen(false);
    setSubmitting(true);
    wsClient.send({
      type: 'vote:cast',
      targetId: selection === ABSTAIN ? null : selection,
    });
  };

  const useAbility = (targetId: string | null) => {
    wsClient.send({ type: 'ability:use', targetId });
    setAbilityOpen(false);
  };

  return (
    <AppShell
      header={<GameHeader view={view} />}
      footer={
        canVote ? (
          <Button
            fullWidth
            disabled={selection === null}
            pending={submitting}
            onClick={() => setConfirmOpen(true)}
          >
            {de.voting.submit}
          </Button>
        ) : undefined
      }
    >
      <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-4">
        <Timer
          endsAt={view.phaseEndsAt}
          totalSeconds={totalSeconds}
          size={140}
          label={de.common.timerVoting}
        />

        <h1 className="text-center font-display text-xl font-bold">
          {ballot.isTieBreaker ? de.voting.tieBreakerTitle : de.voting.title}
        </h1>

        {!view.self.canVote ? (
          <p className="text-center text-sm text-fg-muted">{de.voting.spectating}</p>
        ) : alreadyVoted ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <p className="font-display text-lg font-bold text-accent">{de.voting.submitted}</p>
            <p className="text-sm text-fg-muted">{de.voting.waiting}</p>
          </div>
        ) : null}

        <VoteProgress voted={ballot.votedCount} eligible={ballot.eligibleCount} />

        <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-3">
          {ballot.candidateIds
            .filter((id) => id !== view.self.id)
            .map((id) => {
              const player = view.players.find((p) => p.id === id);
              if (!player) return null;
              const active = selection === id;
              return (
                <button
                  key={id}
                  type="button"
                  disabled={!canVote}
                  onClick={() => {
                    // Sofortige Rückmeldung auf den Tap. `haptics.voteCast` kommt
                    // erst mit dem Server-Echo und bedeutet etwas anderes:
                    // „angekommen", nicht „ausgewählt".
                    haptics.tick();
                    setSelection(id);
                  }}
                  aria-pressed={active}
                  className={`relative flex min-h-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 px-3 py-4 font-display font-semibold transition-colors disabled:opacity-50 ${
                    active
                      ? 'border-accent bg-accent-muted text-accent'
                      : 'border-surface-border bg-surface text-fg'
                  }`}
                >
                  <VoteStatus hasVoted={player.hasVoted} />
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-sm font-bold text-black/80"
                    style={{ background: avatarColor(player.name) }}
                    aria-hidden="true"
                  >
                    {player.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="max-w-full truncate">{player.name}</span>
                  {active && <span className="text-xs font-medium">{de.voting.selected}</span>}
                </button>
              );
            })}

          {view.settings.allowAbstain && (
            <button
              type="button"
              disabled={!canVote}
              onClick={() => {
                haptics.tick();
                setSelection(ABSTAIN);
              }}
              aria-pressed={selection === ABSTAIN}
              className={`flex min-h-28 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed px-3 py-4 font-display font-semibold transition-colors disabled:opacity-50 ${
                selection === ABSTAIN
                  ? 'border-accent bg-accent-muted text-accent'
                  : 'border-surface-border text-fg-muted'
              }`}
            >
              {de.voting.abstain}
            </button>
          )}
        </div>

        {specialRole && (
          <AbilityPanel
            view={view}
            role={specialRole}
            onOpen={() => setAbilityOpen(true)}
            onUse={useAbility}
          />
        )}
      </div>

      <ConfirmSheet
        open={confirmOpen}
        title={
          selectedName === null
            ? de.voting.confirmAbstainTitle
            : de.voting.confirmTitle(selectedName)
        }
        body={de.voting.confirmBody}
        confirmLabel={de.voting.confirmCta}
        onConfirm={submit}
        onCancel={() => setConfirmOpen(false)}
      />

      {specialRole && (
        <Sheet
          open={abilityOpen}
          onClose={() => setAbilityOpen(false)}
          title={de.specialRoles[specialRole].pickTitle}
        >
          <ul className="flex flex-col gap-2">
            {abilityTargets(view, specialRole).map((player) => (
              <li key={player.id}>
                <button
                  type="button"
                  onClick={() => useAbility(player.id)}
                  className="min-h-12 w-full rounded-xl border border-surface-border bg-bg px-4 py-3 text-left font-medium"
                >
                  {player.name}
                </button>
              </li>
            ))}
          </ul>
        </Sheet>
      )}
    </AppShell>
  );
}

/** Zähler plus Balken — auf einen Blick erfassbar, ohne zwei Zahlen zu vergleichen. */
function VoteProgress({ voted, eligible }: { voted: number; eligible: number }) {
  const fraction = eligible > 0 ? Math.min(1, voted / eligible) : 0;

  return (
    <div className="flex w-full max-w-xs flex-col gap-1.5">
      <p className="text-center text-sm text-fg-muted">{de.voting.progress(voted, eligible)}</p>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-300"
          style={{ width: `${fraction * 100}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Wer schon gewählt hat — als Ecke der Auswahlkachel statt als zweite Liste
 * darunter.
 *
 * Vorher stand jeder Name zweimal auf dem Screen: einmal als Kachel, einmal in
 * einer Statusliste. Bei zehn Spielern hieß das Scrollen in der zeitkritischsten
 * Phase, obwohl GDD §38 dort ausdrücklich wenig Text verlangt.
 *
 * Häkchen gegen Kringel, nicht Farbe gegen Farbe — der Unterschied muss auch
 * ohne Farbwahrnehmung tragen (GDD §33, §39). Alle Kandidaten leben, deshalb
 * ist `hasVoted` hier immer aussagekräftig: für Ausgeschiedene unterdrückt die
 * Projektion das Flag, damit die Stimme aus dem Jenseits („Letztes Wort") nicht
 * namentlich aufleuchtet.
 */
function VoteStatus({ hasVoted }: { hasVoted: boolean }) {
  return (
    <span className="absolute top-2 right-2 text-xs font-medium">
      <span aria-hidden="true" className={hasVoted ? 'text-accent' : 'text-fg-faint'}>
        {hasVoted ? '✓' : '○'}
      </span>
      <span className="sr-only">{hasVoted ? de.voting.hasVoted : de.voting.stillVoting}</span>
    </span>
  );
}

/** Wählbare Ziele je Fähigkeit — Teilmenge dessen, was der Server akzeptiert. */
function abilityTargets(view: RoomView, role: SpecialRole) {
  const others = view.players.filter((player) => player.id !== view.self.id);
  return role === 'BLESSING'
    ? others.filter((player) => view.ballot?.candidateIds.includes(player.id))
    : others.filter((player) => player.alive);
}

/**
 * Der Einsatz liegt bewusst in einem eigenen Dialog statt in einem zweiten
 * Raster neben dem Wahlraster: nebeneinander wäre viel zu leicht jemand
 * beschützt, den man eigentlich rauswählen wollte.
 */
function AbilityPanel({
  view,
  role,
  onOpen,
  onUse,
}: {
  view: RoomView;
  role: SpecialRole;
  onOpen: () => void;
  onUse: (targetId: string | null) => void;
}) {
  const { abilityAvailable, abilityUse } = view.self;

  if (abilityUse !== null) {
    const target = abilityUse.targetId
      ? view.players.find((player) => player.id === abilityUse.targetId)
      : undefined;
    return (
      <p className="rounded-xl border border-accent/40 bg-accent-muted px-4 py-2.5 text-center text-sm font-medium text-accent">
        {target ? de.voting.abilityOn(target.name) : de.specialRoles[role].activeLabel}
      </p>
    );
  }

  // „Letztes Wort" ist die einzige Fähigkeit, die den Tod voraussetzt statt ihn
  // auszuschließen — sonst böte der Button etwas an, das der Server ablehnt.
  const usableNow = role === 'DEAD_REDEMPTION' ? !view.self.alive : view.self.alive;
  if (!abilityAvailable || !usableNow) return null;

  return (
    <Button
      variant="secondary"
      fullWidth
      onClick={() => (NEEDS_TARGET[role] ? onOpen() : onUse(null))}
    >
      {de.specialRoles[role].emoji} {de.specialRoles[role].useCta}
    </Button>
  );
}
